import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  output,
  resource,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { RawSkinResponse, UpgraderNewService } from '../../services/upgrader-new.service';
import { SessionService } from '@core/services/session.service';
import { SocketService } from '@core/services/socket.service';
import { Skin } from '@core/interfaces/skin.interface';
import { SearchInputComponent } from '@core/components/search-input/search-input.component';
import { CheckboxComponent } from '@core/components/checkbox/checkbox.component';
import { ToggleComponent } from '@core/components/toggle/toggle.component';
import { SkinsListComponent } from '@core/components/skins-list/skins-list.component';
import { LoadingIconComponent } from '@core/components/loading-icon/loading-icon.component';
import { TablePaginationComponent } from '@core/components/table/table-pagination/table-pagination.component';
import { fadeInAnimation, rotateRefreshAnimation } from '@core/utils/animations.util';
import { createUpgraderGridBreakpoint } from '../../upgrader-grid.util';
import { normalizeSkinImage } from '@core/utils/skins.util';
import { sleep } from '@core/utils/sleep.util';
import {
  DEFAULT_SKIN_COLOR,
  getNextSortOption,
  REFRESH_DELAY_MS,
  SORT_OPTIONS,
  SortOption,
} from '../../upgrader.constants';

@Component({
  selector: 'app-upgrader-target-items',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    SearchInputComponent,
    CheckboxComponent,
    ToggleComponent,
    SkinsListComponent,
    LoadingIconComponent,
    TablePaginationComponent,
  ],
  templateUrl: './upgrader-target-items.component.html',
  styleUrl: './upgrader-target-items.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInAnimation, rotateRefreshAnimation],
})
export class UpgraderTargetItemsComponent {
  readonly upgraderService = inject(UpgraderNewService);
  readonly sessionService = inject(SessionService);
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gridBreakpoint = createUpgraderGridBreakpoint(this.destroyRef);
  readonly gridConfig  = computed(() => ({ columns: this.gridBreakpoint().columns }));
  private readonly PAGE_SIZE = computed(() => this.gridBreakpoint().pageSize);

  interacted = output<void>();

  // Local state
  readonly search = signal('');
  readonly showPureSkins = signal(false);
  readonly isRefreshing = signal(false);

  readonly sortOptions = SORT_OPTIONS;
  readonly currentSort = signal<SortOption>(SORT_OPTIONS[0]);

  // Load skins using resource (same pattern as inventory-template)
  skinsResource = resource({
    loader: async () => {
      const response: RawSkinResponse[] = await this.socketService.emit('upgrader_items');
      return response?.filter(Boolean) ?? [];
    },
  });

  // Transform raw skins to Skin interface
  readonly targetSkins = computed<Skin[]>(() => {
    return this.skinsResource.value()?.map((item: RawSkinResponse) => ({
      ...item,
      name: item.market_hash_name || item.name || '',
      image: normalizeSkinImage(item.image),
      color: item.color || item.name_color || DEFAULT_SKIN_COLOR,
    })) ?? [];
  });

  // Filtered and sorted skins
  readonly displayedSkins = computed(() => {
    let skins = this.targetSkins();
    const balanceType = this.sessionService.balanceType();
    const pureSkinNames = this.sessionService.pureSkins();

    // Filter by balance type — prevent balance/item type mismatch
    if (balanceType === 'skins') {
      skins = skins.filter(s => !pureSkinNames.includes(s.name));
    } else if (balanceType === 'pure') {
      skins = skins.filter(s => pureSkinNames.includes(s.name));
    }

    // Filter by search
    const searchTerm = this.search().toLowerCase();
    if (searchTerm) {
      skins = skins.filter(s =>
        s.name.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by pure skins if enabled
    if (this.showPureSkins()) {
      skins = skins.filter(s => pureSkinNames.includes(s.name));
    }

    // Sort
    const sortKey = this.currentSort().key;
    skins = [...skins].sort((a, b) => {
      if (sortKey === 'highest_price') {
        return b.price - a.price;
      }
      return a.price - b.price;
    });

    return skins;
  });

  // Pagination
  readonly pageIndex = signal(0);
  readonly totalPages = computed(() => Math.ceil(this.displayedSkins().length / this.PAGE_SIZE()));
  readonly paginatedSkins = computed(() => {
    const size = this.PAGE_SIZE();
    const start = this.pageIndex() * size;
    return this.displayedSkins().slice(start, start + size);
  });

  readonly selectedSkins = computed(() => {
    return this.upgraderService.selectedTargetSkins();
  });

  readonly isLoading = computed(() => this.skinsResource.isLoading());
  readonly error = computed(() => this.skinsResource.error() ? 'Failed to load items' : null);

  // Computed signal for disabling selection during non-idle states
  readonly isSelectionDisabled = computed(() => this.upgraderService.state() !== 'idle');

  constructor() {
    effect(() => {
      this.search();
      this.currentSort();
      this.showPureSkins();
      this.pageIndex.set(0);
    });

    // Auto-deselect target skins that don't match the current balance type
    effect(() => {
      this.sessionService.balanceType();
      this.upgraderService.filterTargetSkinsForBalance();
    });
  }

  async refreshItems() {
    this.isRefreshing.set(true);
    this.search.set('');
    this.upgraderService.clearTargetSkins();
    this.skinsResource.reload();
    await sleep(REFRESH_DELAY_MS);
    this.isRefreshing.set(false);
  }

  toggleSort() {
    this.currentSort.set(getNextSortOption(this.currentSort()));
  }

  onSkinSelect(skin: Skin) {
    if (this.isSelectionDisabled()) return;
    this.interacted.emit();
    this.upgraderService.selectTargetSkin(skin);
  }

  onSearchChange(value: string) {
    this.interacted.emit();
    this.search.set(value);
  }

  onPureToggle(value: boolean) {
    this.interacted.emit();
    this.showPureSkins.set(value);
  }

  nextPage() { this.pageIndex.update(i => Math.min(i + 1, this.totalPages() - 1)); }
  prevPage() { this.pageIndex.update(i => Math.max(i - 1, 0)); }
}
